import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authApi, SignupRequestDto, UserDTO } from "@/services/authService";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "@/lib/api";

interface AuthContextType {
  user: UserDTO | null;
  setUser: (user: UserDTO | null) => void;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (data: SignupRequestDto) => Promise<boolean>;
  resetPassword: (password: string, token: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasPermission: (allowedRoles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast, clear } = useToast();
  const navigate = useNavigate();

  // Mutation for login
  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.login(email, password),

    onSuccess: ({ token, user }) => {
      // Persist token & user
      localStorage.setItem("authToken", token);
      localStorage.setItem("currentUser", JSON.stringify(user));
      setUser(user);
      toast({
        title: "Logged in successfully",
        description: `Welcome back, ${user.firstName} ${user.lastName}!`,
      });
      queryClient.invalidateQueries();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      console.error("Login error:", err);
      toast({
        title: "Login failed",
        description: err?.response?.data?.error || "Unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  // Load session on mount
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const stored = localStorage.getItem("currentUser");
    if (token && stored) {
      try {
        const parsed: UserDTO = JSON.parse(stored);
        setUser(parsed);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      } catch {
        localStorage.removeItem("currentUser");
        localStorage.removeItem("authToken");
      }
    }
    setIsLoading(false);
  }, []);

  const signupMutation = useMutation({
    mutationFn: (data: SignupRequestDto) => authApi.signup(data),
    onSuccess: ({ token, user }) => {
      localStorage.setItem("authToken", token);
      localStorage.setItem("currentUser", JSON.stringify(user));
      setUser(user);
      toast({
        title: "Account created",
        description: `Welcome, ${user.firstName} ${user.lastName}!`,
      });
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      toast({
        title: "Signup failed",
        description: axios.isAxiosError(error)
          ? error.response?.data?.error
          : "Unknown error occurred",
        variant: "destructive",
      });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ password, token }: { password: string; token: string }) =>
      authApi.resetPassword(password, token),
    onSuccess: ({ token: authToken, user }) => {
      localStorage.setItem("authToken", authToken);
      localStorage.setItem("currentUser", JSON.stringify(user));
      setUser(user);
      axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
      toast({
        title: "Password reset successful",
        description: "You have been logged in with your new password.",
      });
    },
    onError: (error) => {
      const isInvalidToken =
        axios.isAxiosError(error) && error.response?.status === 401;
      toast({
        title: isInvalidToken
          ? "Invalid or expired link"
          : "Password reset failed",
        description: isInvalidToken
          ? "This password reset link is invalid or has expired. Please request a new one."
          : axios.isAxiosError(error)
          ? error.response?.data?.error
          : "Unknown error occurred",
        variant: "destructive",
      });
    },
  });

  const signup = async (data: SignupRequestDto): Promise<boolean> => {
    try {
      await signupMutation.mutateAsync(data);
      return true;
    } catch {
      return false;
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      await loginMutation.mutateAsync({ email, password });
      return true;
    } catch {
      return false;
    }
  };

  const resetPassword = async (
    password: string,
    token: string
  ): Promise<boolean> => {
    try {
      await resetPasswordMutation.mutateAsync({ password, token });
      return true;
    } catch {
      return false;
    }
  };
  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("currentUser");
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
    toast({
      title: "Logged out",
      description: "You have been logged out successfully",
    });
    queryClient.invalidateQueries();
    queryClient.cancelQueries();
    queryClient.clear();
    clear();
    
    // Navigate to login without any redirect parameters
    navigate("/login", { replace: true });
  };

  const hasPermission = (allowedRoles: string[]): boolean => {
    if (!user) return false;
    // Super admins have access to everything
    if (user.superAdmin) return true;
    return allowedRoles.includes(user.role);
  };

  const isLoadingState =
    isLoading || loginMutation.isPending || signupMutation.isPending;

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        login,
        logout,
        signup,
        resetPassword,
        isLoading: isLoadingState,
        isAuthenticated: !!user,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
